import { useState, useEffect, useRef } from 'react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { COMPANY, SOCIALS } from '../constants/company'
import TeamCard from '../components/TeamCard'
import { useLanguage } from '../context/LanguageContext'

const NAVY = '#002E53'
const GOLD = '#b08d57'
const FONT = "'Poppins', sans-serif"

function ContactPage() {
  const { t } = useLanguage()
  const c = t('contact')
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: '', message: '',
  })
  const [submitted, setSubmitted] = useState(false)
  const [teamMembers, setTeamMembers] = useState([])
  const cpTrackRef = useRef(null)

  useEffect(() => {
    fetch('/api/team')
      .then(res => res.json())
      .then(data => setTeamMembers(Array.isArray(data) ? data : []))
      .catch(err => console.error('Error fetching team members:', err))
  }, [])

  const pauseCpTrack  = () => { if (cpTrackRef.current) cpTrackRef.current.style.animationPlayState = 'paused' }
  const resumeCpTrack = () => { if (cpTrackRef.current) cpTrackRef.current.style.animationPlayState = 'running' }

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const res = await fetch('/api/admin/contact-messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form: formData })
      })
      if (!res.ok) throw new Error('Submission failed')
      setSubmitted(true)
      setTimeout(() => setSubmitted(false), 4000)
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' })
    } catch (err) {
      alert('Failed to send message. Please try again.')
    }
  }

  return (
    <div className="website-wrapper wpresidence_wrapper_for_header_type1 wide" id="all_wrapper">
      <div className="container-fluid px-0 wpresidence_main_wrapper_class wide has_header_type1">
        <Navbar />
        <main style={{ backgroundColor: '#f8f9fa', minHeight: '80vh' }}>

          {/* Hero banner */}
          <div style={{
            background: `linear-gradient(135deg, ${NAVY} 0%, #001a33 100%)`,
            padding: 'clamp(100px, 12vw, 150px) 20px clamp(50px, 7vw, 80px)',
            textAlign: 'center',
          }}>
            <h1 style={{
              fontFamily: FONT, fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: 600,
              color: '#fff', margin: '0 0 14px 0', letterSpacing: '-0.5px',
            }}>
              {c.heroHeading}
            </h1>
            <p style={{
              fontFamily: FONT, fontSize: '16px', fontWeight: 400,
              color: 'rgba(255,255,255,0.7)', margin: 0, maxWidth: '520px',
              marginLeft: 'auto', marginRight: 'auto', lineHeight: '1.6',
            }}>
              {c.heroSub}
            </p>
          </div>

          {/* ── MEET THE TEAM SECTION — MARQUEE ── */}
          <div style={{ backgroundColor: '#fff', paddingTop: '70px', paddingBottom: '30px' }}>
            <style>{`
              .cp-team-marquee-container {
                width: 100%;
                overflow: hidden;
                position: relative;
                padding: 8px 0 20px;
              }
              .cp-team-marquee-container::before,
              .cp-team-marquee-container::after {
                content: '';
                position: absolute;
                top: 0; height: 100%;
                width: 160px;
                z-index: 2;
                pointer-events: none;
              }
              .cp-team-marquee-container::before {
                left: 0;
                background: linear-gradient(to right, #ffffff 0%, rgba(255,255,255,0) 100%);
              }
              .cp-team-marquee-container::after {
                right: 0;
                background: linear-gradient(to left, #ffffff 0%, rgba(255,255,255,0) 100%);
              }
              .cp-team-marquee-track {
                display: flex;
                width: max-content;
                gap: 24px;
                animation: cp-team-scroll 28s linear infinite;
              }
              /* Pause handled via JS mouse events — NOT CSS :hover
                 so touch devices keep scrolling uninterrupted */
              @keyframes cp-team-scroll {
                0%   { transform: translateX(0); }
                100% { transform: translateX(-50%); }
              }
              .cp-team-marquee-card { flex: 0 0 280px; width: 280px; }
              @media (max-width: 540px) {
                .cp-team-marquee-card { flex: 0 0 240px; width: 240px; }
                .cp-team-marquee-container::before,
                .cp-team-marquee-container::after { width: 60px; }
              }
            `}</style>

            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '32px', padding: '0 20px' }}>
              <span style={{
                display: 'inline-block', background: `${GOLD}22`, color: GOLD,
                fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px',
                padding: '4px 12px', borderRadius: '50px', textTransform: 'uppercase',
                border: `1px solid ${GOLD}40`, marginBottom: '10px',
              }}>{c.expertsBadge}</span>
              <h2 style={{
                fontFamily: FONT, fontSize: 'clamp(26px, 3.5vw, 42px)', fontWeight: 600,
                color: NAVY, margin: 0, letterSpacing: '-0.3px',
              }}>{c.expertsHeading}</h2>
            </div>

            {/* Marquee belt — mouse events pause; touch events are ignored */}
            <div
              className="cp-team-marquee-container"
              onMouseEnter={pauseCpTrack}
              onMouseLeave={resumeCpTrack}
            >
              <div ref={cpTrackRef} className="cp-team-marquee-track">
                {teamMembers.length > 0 && [...teamMembers, ...teamMembers].map((member, i) => (
                  <div key={`${member.id}-${i}`} className="cp-team-marquee-card">
                    <TeamCard member={member} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── CONTACT FORM & INFO ── */}
          <div
            className="contact-grid"
            style={{
              maxWidth: '1300px', margin: '0 auto', padding: '60px 20px 80px',
              display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: '50px',
              boxSizing: 'border-box',
            }}
          >
            <style>{`
              @media (max-width: 768px) {
                .contact-grid { grid-template-columns: 1fr !important; }
              }
              .contact-inner-form-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 18px;
              }
              @media (max-width: 520px) {
                .contact-inner-form-grid { grid-template-columns: 1fr !important; }
              }
              .contact-input {
                width: 100%; padding: 16px 20px;
                border: 1px solid #e2e8f0; border-radius: 12px;
                font-family: ${FONT}; font-size: 15px;
                color: ${NAVY}; background: #f8fafc;
                outline: none; transition: all 0.3s ease;
                box-sizing: border-box;
                box-shadow: inset 0 2px 4px rgba(0,0,0,0.02);
              }
              .contact-input:focus { 
                border-color: ${GOLD}; 
                background: #fff;
                box-shadow: 0 0 0 4px rgba(176,141,87,0.1);
              }
              .contact-input::placeholder { color: #9ca3af; }
              .contact-submit {
                background: ${NAVY}; color: #fff;
                font-family: ${FONT}; font-size: 15px; font-weight: 500;
                padding: 15px 40px; border: none; border-radius: 8px;
                cursor: pointer; transition: background 0.2s, transform 0.15s;
                width: 100%;
              }
              .contact-submit:hover { background: #001a33; transform: translateY(-1px); }
              .contact-submit:active { transform: translateY(0); }
            `}</style>

            {/* LEFT — Contact Info Cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

              {/* Office Location */}
              <div style={{
                backgroundColor: '#fff', borderRadius: '12px', padding: '28px 30px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
              }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '10px',
                    backgroundColor: `${NAVY}0d`, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <i className="fas fa-map-marker-alt" style={{ color: NAVY, fontSize: '18px' }} />
                  </div>
                  <div>
                    <h4 style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 600, color: NAVY, margin: '0 0 6px' }}>
                      {c.officeLabel}
                    </h4>
                    <p style={{ fontFamily: FONT, fontSize: '14px', color: '#5a6577', margin: 0, lineHeight: '1.6' }}>
                      {COMPANY.location}
                    </p>
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div style={{
                backgroundColor: '#fff', borderRadius: '12px', padding: '28px 30px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
              }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '10px',
                    backgroundColor: `${NAVY}0d`, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <i className="fas fa-phone-alt" style={{ color: NAVY, fontSize: '18px' }} />
                  </div>
                  <div>
                    <h4 style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 600, color: NAVY, margin: '0 0 6px' }}>
                      {c.phoneLabel}
                    </h4>
                    <p style={{ fontFamily: FONT, fontSize: '14px', color: '#5a6577', margin: '0 0 4px', lineHeight: '1.6' }}>
                      {c.phoneOffice}: <a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} style={{ color: NAVY, textDecoration: 'none', fontWeight: 500 }}>{COMPANY.phone}</a>
                    </p>
                    <p style={{ fontFamily: FONT, fontSize: '14px', color: '#5a6577', margin: 0, lineHeight: '1.6' }}>
                      {c.phoneWA}: <a href={`https://wa.me/${COMPANY.whatsapp.replace(/\s/g, '')}`} style={{ color: NAVY, textDecoration: 'none', fontWeight: 500 }}>{COMPANY.whatsapp}</a>
                    </p>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div style={{
                backgroundColor: '#fff', borderRadius: '12px', padding: '28px 30px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
              }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '10px',
                    backgroundColor: `${NAVY}0d`, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <i className="fas fa-envelope" style={{ color: NAVY, fontSize: '18px' }} />
                  </div>
                  <div>
                    <h4 style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 600, color: NAVY, margin: '0 0 6px' }}>
                      {c.emailLabel}
                    </h4>
                    <p style={{ fontFamily: FONT, fontSize: '14px', color: '#5a6577', margin: 0, lineHeight: '1.6' }}>
                      <a href={`mailto:${COMPANY.email}`} style={{ color: NAVY, textDecoration: 'none', fontWeight: 500 }}>{COMPANY.email}</a>
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div style={{
                backgroundColor: '#fff', borderRadius: '12px', padding: '28px 30px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
              }}>
                <h4 style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 600, color: NAVY, margin: '0 0 16px' }}>
                  {c.followLabel}
                </h4>
                <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                  {[
                    { href: SOCIALS.facebook, icon: 'fab fa-facebook-f' },
                    { href: SOCIALS.instagram, icon: 'fab fa-instagram' },
                    { href: SOCIALS.linkedin, icon: 'fab fa-linkedin-in' },
                    { href: SOCIALS.x, icon: 'fa-brands fa-x-twitter' },
                    { href: SOCIALS.youtube, icon: 'fab fa-youtube' },
                    { href: SOCIALS.tiktok, icon: 'fab fa-tiktok' },
                  ].map(({ href, icon }) => (
                    <a key={icon} href={href} target="_blank" rel="noreferrer"
                      style={{
                        width: '40px', height: '40px', borderRadius: '8px',
                        backgroundColor: `${NAVY}0d`, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        color: NAVY, fontSize: '16px', textDecoration: 'none',
                        transition: 'background 0.2s, color 0.2s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = NAVY; e.currentTarget.style.color = '#fff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = `${NAVY}0d`; e.currentTarget.style.color = NAVY; }}
                    >
                      <i className={icon} />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT — Contact Form */}
            <div style={{
              backgroundColor: '#fff', borderRadius: '12px', padding: '40px 36px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
              alignSelf: 'flex-start',
            }}>
              <h3 style={{
                fontFamily: FONT, fontSize: '24px', fontWeight: 600,
                color: NAVY, margin: '0 0 6px',
              }}>
                {c.formHeading}
              </h3>
              <p style={{
                fontFamily: FONT, fontSize: '14px', color: '#5a6577',
                margin: '0 0 30px', lineHeight: '1.5',
              }}>
                {c.formSub}
              </p>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                <div className="contact-inner-form-grid">
                  <input
                    className="contact-input" type="text" name="name"
                    placeholder={c.fieldName} required
                    value={formData.name} onChange={handleChange}
                  />
                  <input
                    className="contact-input" type="email" name="email"
                    pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                    title="Please enter a valid email address"
                    placeholder="Email Address *" value={formData.email} onChange={handleChange} required
                  />
                </div>
                <div className="contact-inner-form-grid">
                  <input
                    className="contact-input" type="tel" name="phone"
                    placeholder={c.fieldPhone}
                    value={formData.phone} onChange={handleChange}
                  />
                  <input
                    className="contact-input" type="text" name="subject"
                    placeholder={c.fieldSubject}
                    value={formData.subject} onChange={handleChange}
                  />
                </div>
                <textarea
                  className="contact-input" name="message"
                  placeholder={c.fieldMessage} required rows={6}
                  value={formData.message} onChange={handleChange}
                  style={{ resize: 'vertical', minHeight: '140px' }}
                />

                <button type="submit" className="contact-submit">
                  {submitted ? c.sentBtn : c.sendBtn}
                </button>

                {submitted && (
                  <p style={{
                    fontFamily: FONT, fontSize: '13px', color: '#16a34a',
                    margin: 0, textAlign: 'center',
                  }}>
                    {c.thankYou}
                  </p>
                )}
              </form>
            </div>
          </div>

          {/* Google Map / Location visual */}
          <div style={{
            width: '100%', height: '400px', backgroundColor: '#e5e7eb',
            position: 'relative', overflow: 'hidden',
          }}>
            <iframe
              title="Victory Homes Office Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3609.9!2d55.3025!3d25.2532!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f43496ad9c645%3A0xbde66e2e4cbb601!2sBurJuman%20Centre!5e0!3m2!1sen!2sae!4v1710000000000"
              style={{ border: 0, width: '100%', height: '100%' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

        </main>
        <Footer />
      </div>
    </div>
  )
}

export default ContactPage
